import { t } from "ttag";

import { DelayedSkeleton } from "metabase/common/components/DelayedLoading";
import { EmptyState } from "metabase/common/components/EmptyState";
import { EntityIcon } from "metabase/common/components/EntityIcon";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { VirtualizedList } from "metabase/common/components/VirtualizedList";
import { NoObjectError } from "metabase/common/components/errors/NoObjectError";
import { useGetIcon } from "metabase/hooks/use-icon";
import { PLUGIN_LIBRARY, PLUGIN_MODERATION } from "metabase/plugins";
import {
  Box,
  Ellipsified,
  Flex,
  Icon,
  NavLink,
  SegmentedControl,
  Text,
} from "metabase/ui";

import type {
  OmniPickerItem,
  OmniPickerMeasureItem,
  OmniPickerTableItem,
  SearchScope,
} from "../..";
import { useOmniPickerContext } from "../../context";
import {
  useCurrentSearchScope,
  useGetLastCollection,
} from "../../hooks/use-current-search-scope";
import { getDataAppsRootItem } from "../../hooks/utils";
import { isSelectedItem, useGetEntityPickerIcon } from "../../utils";

export const SearchResults = ({
  searchResults,
  isLoading,
  error,
  onClick,
}: {
  searchResults: OmniPickerItem[];
  isLoading?: boolean;
  error?: unknown;
  onClick?: (item: OmniPickerItem, index: number) => void;
}) => {
  const { path, setPath, isDisabledItem, isSelectableItem, options, onChange } =
    useOmniPickerContext();
  const selectedItem = path?.[path.length - 1];
  const getEntityPickerIcon = useGetEntityPickerIcon();

  if (error) {
    return (
      <Box h="100%" w="40rem">
        <LoadingAndErrorWrapper error={error} />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box h="100%" w="40rem" p="lg">
        <DelayedSkeleton />
      </Box>
    );
  }

  if (searchResults.length === 0) {
    return (
      <EmptyState
        title={t`We didn't find anything`}
        illustrationElement={<NoObjectError mb="-1.5rem" />}
      />
    );
  }

  return (
    <VirtualizedList
      Wrapper={({ children, ...props }) => (
        <Box py="lg" {...props}>
          {children}
        </Box>
      )}
      estimatedItemSize={66}
    >
      {searchResults?.map((item, index) => {
        const isSelected = isSelectedItem(item, selectedItem);
        const isSelectable = isSelectableItem(item);

        return (
          <Box
            key={`${item.model}-${item.id}`}
            pb="xxs"
            px="sm"
            data-testid="result-item"
            data-model-type={item.model}
          >
            <NavLink
              w={"auto"}
              disabled={isDisabledItem(item) || !isSelectable}
              rightSection={
                <LocationInfo item={item} isSelected={isSelected} />
              }
              mb={0}
              label={
                <Flex align="center">
                  <Ellipsified maw="21rem">{item.name} </Ellipsified>
                  <PLUGIN_MODERATION.ModerationStatusIcon
                    status={"moderated_status" in item && item.moderated_status}
                    filled
                    size={14}
                    ml="0.5rem"
                  />
                </Flex>
              }
              active={isSelected}
              leftSection={
                <EntityIcon
                  {...getEntityPickerIcon(item, { isSelected })}
                  size="1rem"
                />
              }
              onClick={(e: React.MouseEvent) => {
                e.preventDefault(); // prevent form submission
                e.stopPropagation(); // prevent parent onClick

                if (onClick) {
                  onClick(item, index);
                }

                if (isSelectable) {
                  setPath((prevPath) => [...prevPath.slice(0, 1), item]);

                  if (!options?.hasConfirmButtons) {
                    onChange(item);
                  }
                }
              }}
              variant="primary"
            />
          </Box>
        );
      })}
    </VirtualizedList>
  );
};

const isTableInDb = (item: OmniPickerItem): item is OmniPickerTableItem => {
  return (
    item.model === "table" &&
    (!("collection" in item) || (!!item.collection && !item.collection.name))
  );
};

const isMeasure = (item: OmniPickerItem): item is OmniPickerMeasureItem => {
  return item.model === "measure";
};

const getItemText = (item: OmniPickerItem) => {
  const isTable = isTableInDb(item);

  if (item.model === "schema") {
    return item.id;
  }

  if (item.model === "database") {
    return "";
  }

  if (item.model === "data-app") {
    return getDataAppsRootItem().name;
  }

  if (isMeasure(item)) {
    return item.table_display_name ?? item.table_name;
  }

  return isTable
    ? `${item.database_name}${item.schema ? ` (${item.schema})` : ""}`
    : (item?.collection?.name ?? t`Our analytics`);
};

const useLocationIcon = (item: OmniPickerItem) => {
  const getIcon = useGetIcon();

  if (
    item.model === "table" ||
    item.model === "schema" ||
    item.model === "database"
  ) {
    return null;
  }

  if (isMeasure(item)) {
    return { name: "table" as const };
  }

  return getIcon({
    ...item,
    model: "collection",
  });
};

const LocationInfo = ({
  item,
  isSelected,
}: {
  item: OmniPickerItem;
  isSelected: boolean;
}) => {
  const itemText = getItemText(item);
  const iconProps = useLocationIcon(item);

  if (!itemText) {
    return null;
  }

  return (
    <Flex gap="xxs" align="center">
      {iconProps && <Icon {...iconProps} size={12} />}
      <Text
        size="sm"
        c={isSelected ? "text-secondary-inverse" : "text-secondary"}
      >
        <Ellipsified maw="12rem" data-testid="picker-item-location">
          {itemText}
        </Ellipsified>
      </Text>
    </Flex>
  );
};

export function SearchScopeSelector() {
  const { setSearchScope, options: pickerOptions } = useOmniPickerContext();
  const searchScope = useCurrentSearchScope();

  const { data: libraryCollection } = PLUGIN_LIBRARY.useGetLibraryCollection();

  const lastCollection = useGetLastCollection();

  const options = [
    { label: t`Everywhere`, value: "all" },
    libraryCollection?.id
      ? { label: t`Library`, value: String(libraryCollection.id) }
      : null,
    lastCollection
      ? {
          label: lastCollection.name,
          value: String(lastCollection.id),
        }
      : null,
  ].filter((i) => i !== null);

  if (pickerOptions.disableSearchScope) {
    return null;
  }

  return (
    <Flex
      justify="space-between"
      align="center"
      px="lg"
      py="sm"
      bg="background_page-secondary"
      mb="xxs"
      data-testid="search-scope-selector"
    >
      <Text>{t`Where to search:`}</Text>
      <SegmentedControl
        labelMaxWidth="20ch"
        value={searchScope ? String(searchScope) : "all"}
        // Unjustified type cast. FIXME
        onChange={(newValue) => setSearchScope(newValue as SearchScope)}
        data={options}
      />
    </Flex>
  );
}
